/**
 * Auth calls and the WebAuthn ceremonies.
 *
 * Transport belongs to A4's `lib/api`. This file knows only the auth endpoints and the two
 * browser ceremonies, so there is one place to look when the login flow misbehaves.
 *
 * Two things the UI must never do, and therefore cannot do from here:
 *   - tell the operator whether a handle exists. Every failure is the same message.
 *   - use a PIN for anything except unlocking a session or logging in from a device that
 *     already holds a device cookie. There is no PIN path to enrollment in this file.
 */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { Session, StepUp, StepUpTier } from '@elutide/shared';
import { ApiProblem, api } from '@/lib/api';

export interface LoginCeremony {
  ceremony_id: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface RegisterCeremony {
  ceremony_id: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

/**
 * What `POST /auth/step-up/begin` answers. In passkey mode it is a WebAuthn ceremony that
 * may also allow a PIN at tier 2. In PIN-only mode the server starts no ceremony at all
 * and answers `{ method: 'pin', pin_allowed: true }`, at any tier.
 */
export type StepUpCeremony =
  | (LoginCeremony & { method: 'passkey'; pin_allowed: boolean })
  | { method: 'pin'; pin_allowed: true; ceremony_id?: undefined; options?: undefined };

/** The one sentence the login screen is allowed to say when anything goes wrong. */
export const GENERIC_AUTH_FAILURE = 'That did not work. Check the device and try again.';

/**
 * Turn any failure into the same message, except the two the operator can actually act on:
 * a lockout, and a cancelled or unsupported WebAuthn ceremony.
 */
export function loginErrorMessage(error: unknown): string {
  if (error instanceof ApiProblem) {
    if (error.code === 'rate_limited') return error.message;
    return GENERIC_AUTH_FAILURE;
  }
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'That was cancelled or timed out. Try again.';
    if (error.name === 'InvalidStateError') return 'This device already has a passkey for that account.';
    if (error.name === 'AbortError') return 'That was cancelled.';
  }
  return GENERIC_AUTH_FAILURE;
}

/* ------------------------------------------------------------------ */
/* Session                                                             */
/* ------------------------------------------------------------------ */

export function getSession(): Promise<Session> {
  return api.get<Session>('/auth/session');
}

export function lockSession(): Promise<void> {
  return api.post<void>('/auth/lock');
}

export function logout(): Promise<void> {
  return api.post<void>('/auth/logout');
}

/* ------------------------------------------------------------------ */
/* Passkey login                                                       */
/* ------------------------------------------------------------------ */

/**
 * The handle is optional and the server ignores it: options come back identical whether or
 * not an account exists, which is what keeps the login screen free of enumeration.
 */
export async function passkeyLogin(handle?: string): Promise<Session> {
  const ceremony = await api.post<LoginCeremony>('/auth/passkey/login/begin', handle ? { handle } : {});
  const response = await startAuthentication({ optionsJSON: ceremony.options });
  return api.post<Session>('/auth/passkey/login/complete', {
    ceremony_id: ceremony.ceremony_id,
    response,
  });
}

/** Unlock a soft-locked session with a passkey. Same ceremony, different completion. */
export async function passkeyUnlock(): Promise<Session> {
  const ceremony = await api.post<LoginCeremony>('/auth/passkey/login/begin', {});
  const response = await startAuthentication({ optionsJSON: ceremony.options });
  return api.post<Session>('/auth/passkey/unlock', {
    ceremony_id: ceremony.ceremony_id,
    response,
  });
}

/* ------------------------------------------------------------------ */
/* PIN                                                                 */
/* ------------------------------------------------------------------ */

/**
 * Passkey mode: handle plus PIN on an enrolled device. PIN mode: the PIN alone; the server
 * finds the user by verifying it, and the body carries nothing else.
 */
export function pinLogin(input: { pin: string; handle?: string }): Promise<Session> {
  return api.post<Session>('/auth/pin/login', input.handle === undefined ? { pin: input.pin } : { handle: input.handle, pin: input.pin });
}

export function pinUnlock(pin: string): Promise<Session> {
  return api.post<Session>('/auth/pin/unlock', { pin });
}

/* ------------------------------------------------------------------ */
/* Enrollment                                                          */
/* ------------------------------------------------------------------ */

/**
 * Register a passkey. With an enrollment code this is a brand new device being brought in
 * by the owner; without one it is an existing user adding a second authenticator, which
 * the server gates behind a fresh passkey step-up.
 */
export async function registerPasskey(input: {
  enrollmentCode?: string;
  label?: string;
  stepUpId?: string;
}): Promise<{ passkey_id: string }> {
  const begin: Record<string, unknown> = {};
  if (input.enrollmentCode) begin.enrollment_code = input.enrollmentCode;
  if (input.label) begin.label = input.label;

  const ceremony = await api.post<RegisterCeremony>('/auth/passkey/register/begin', begin, {
    ...(input.stepUpId ? { stepUpId: input.stepUpId } : {}),
    noStepUpRetry: false,
  });
  const response: RegistrationResponseJSON = await startRegistration({ optionsJSON: ceremony.options });
  return api.post<{ passkey_id: string }>('/auth/passkey/register/complete', {
    ceremony_id: ceremony.ceremony_id,
    response,
    ...(input.label ? { label: input.label } : {}),
  });
}

/* ------------------------------------------------------------------ */
/* Step-up                                                             */
/* ------------------------------------------------------------------ */

export function beginStepUp(tier: 2 | 3): Promise<StepUpCeremony> {
  return api.post<StepUpCeremony>('/auth/step-up/begin', { tier }, { noStepUpRetry: true });
}

export async function completeStepUpWithPasskey(ceremony: StepUpCeremony): Promise<StepUp> {
  if (ceremony.method !== 'passkey') {
    throw new Error('This console signs in with a PIN, so there is no passkey ceremony to run.');
  }
  const response: AuthenticationResponseJSON = await startAuthentication({
    optionsJSON: ceremony.options,
  });
  return api.post<StepUp>(
    '/auth/step-up/complete',
    { ceremony_id: ceremony.ceremony_id, response },
    { noStepUpRetry: true },
  );
}

/**
 * Passkey mode: tier 2 only, and the server refuses a PIN for tier 3 whatever the client
 * sends. PIN mode: the tier is sent and the server mints a step-up at that tier; tier 3
 * still keeps its typed confirmation on the calling screen.
 */
export function completeStepUpWithPin(pin: string, tier?: 2 | 3): Promise<StepUp> {
  return api.post<StepUp>(
    '/auth/step-up/complete',
    tier === undefined ? { pin } : { pin, tier },
    { noStepUpRetry: true },
  );
}

export function tierLabel(tier: StepUpTier, mode: 'pin' | 'passkey' = 'passkey'): string {
  if (mode === 'pin') return 'your PIN';
  return tier === 3 ? 'a passkey' : 'a passkey or your PIN';
}
